import { PLACEMENT_GAMES } from '@kolony/shared';
import { nextBlurPx, rankBadgeUrl, rankName, railGeometry, type RankView } from './rankInfo';

// The rank rail on the main menu (H14): current rank on the left, the next one on the right,
// a bar between them. Presentation only — it renders a RankView and owns no state.
//
// Two modes, one element. Placed: a rating bar across the current band, with the next rank
// blurred until it has been held. Unplaced: three placement segments and NO SCORE — the
// server does not send one, and a number after one game would be noise we would immediately
// contradict.

export interface RankRail {
  root: HTMLElement;
  update(view: RankView): void;
}

/** The socket size the rail draws at — the blur radius is derived from it. */
const SOCKET_PX = 42;

export function createRankRail(doc: Document): RankRail {
  const root = doc.createElement('section');
  root.className = 'rank-rail';
  root.dataset['rankRail'] = '1';

  const curWrap = doc.createElement('div');
  curWrap.className = 'rank-socket';
  const curImg = doc.createElement('img');
  curImg.className = 'rank-shield';
  curImg.alt = '';
  curImg.decoding = 'async';
  const curName = doc.createElement('div');
  curName.className = 'rank-socket-label';
  curWrap.append(curImg, curName);

  const mid = doc.createElement('div');
  mid.className = 'rank-mid';
  const head = doc.createElement('div');
  head.className = 'rank-head';
  const score = doc.createElement('span');
  score.className = 'rank-score';
  const togo = doc.createElement('span');
  togo.className = 'rank-togo';
  head.append(score, togo);

  const track = doc.createElement('div');
  track.className = 'rank-track';
  const fill = doc.createElement('div');
  fill.className = 'rank-fill';
  track.appendChild(fill);

  const pips = doc.createElement('div');
  pips.className = 'rank-pips';
  const segs: HTMLElement[] = [];
  for (let i = 0; i < PLACEMENT_GAMES; i++) {
    const seg = doc.createElement('span');
    seg.className = 'rank-pip';
    segs.push(seg);
    pips.appendChild(seg);
  }
  mid.append(head, track, pips);

  const nextWrap = doc.createElement('div');
  nextWrap.className = 'rank-socket rank-socket--next';
  const nextImg = doc.createElement('img');
  nextImg.className = 'rank-shield';
  nextImg.alt = '';
  nextImg.decoding = 'async';
  const nextName = doc.createElement('div');
  nextName.className = 'rank-socket-label';
  nextWrap.append(nextImg, nextName);

  root.append(curWrap, mid, nextWrap);

  function update(view: RankView): void {
    root.dataset['rankPlaced'] = view.placed ? '1' : '0';

    if (!view.placed) {
      const left = Math.max(0, view.placementGames - view.ratedGames);
      curImg.src = rankBadgeUrl(null);
      curName.textContent = rankName(null);
      score.textContent = '';
      score.dataset['rankScore'] = 'hidden';
      togo.textContent = left === 1 ? '1 more game to earn your rank' : `${left} more games to earn your rank`;
      track.hidden = true;
      pips.hidden = false;
      segs.forEach((seg, i) => seg.classList.toggle('rank-pip--done', i < view.ratedGames));
      // A hidden shield to be curious about: the goal is the rank itself.
      nextImg.src = rankBadgeUrl(null);
      nextImg.style.filter = `blur(${(SOCKET_PX * 0.1).toFixed(1)}px) brightness(.95)`;
      nextName.textContent = 'Your rank';
      return;
    }

    const rank = view.rank!;
    const geo = railGeometry(view.rating ?? 0, rank);
    curImg.src = rankBadgeUrl(rank);
    curName.textContent = rankName(rank);
    score.textContent = String(Math.round(view.rating ?? 0));
    score.dataset['rankScore'] = 'shown';
    track.hidden = false;
    pips.hidden = true;
    fill.style.width = `${(geo.progress * 100).toFixed(1)}%`;

    if (geo.next === null) {
      togo.textContent = 'Top of the ladder';
      nextImg.removeAttribute('src');
      nextImg.hidden = true;
      nextName.textContent = '';
      return;
    }
    nextImg.hidden = false;
    nextImg.src = rankBadgeUrl(geo.next);
    const held = view.everHeld.includes(geo.next);
    const blur = nextBlurPx(geo.progress, held, SOCKET_PX);
    // Blur hides the IDENTITY on its own; the desaturation only says "not yours yet". An
    // earlier pass also dimmed to 0.62 and the shield disappeared into the dark panel
    // entirely — a goal you cannot see at all is not a goal, it is an empty socket.
    nextImg.style.filter = blur === 0 ? '' : `blur(${blur.toFixed(1)}px) grayscale(.4) brightness(.92)`;
    nextName.textContent = rankName(geo.next);
    togo.textContent = `${Math.max(0, Math.ceil(geo.hi - (view.rating ?? 0)))} to ${rankName(geo.next)}`;
  }

  return { root, update };
}
